import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { can, PREVIOUS_HEATING_FUELS, type JobStage, type PreviousHeatingFuel } from "@sts/schemas";
import type {
  Colleague,
  Deadline,
  Job as JobRow,
  JobCustomer,
  JobCustomerPatch,
  JobSurveySummary,
  Me,
  Task,
} from "../api/types.js";
import { ApiError, ApiUnreachableError, complianceFailures, refusedAction } from "../api/client.js";
import { DeleteAction } from "../components/DeleteAction.jsx";
import { Async, Failure } from "../components/Async.jsx";
import { UrgencyBadge } from "../components/Chips.jsx";
import { IssuedContractLink } from "../components/IssuedContractLink.jsx";
import { PriorityBadge } from "../components/Priority.jsx";
import { usePortal, useSession } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { href } from "../app/router.js";
import { STAGE_LABEL, isClosed, nextStages, requiredDateField } from "../lib/stages.js";
import { ruleFor } from "../lib/deadlines.js";
import { isoDay, longDate } from "../lib/format.js";
import { JobThreads } from "./Chat.jsx";

interface JobDetail {
  readonly job: JobRow;
  /** Null when the role holds `job:read` but not `contact:read`. */
  readonly customer: JobCustomer | null;
  readonly tasks: readonly Task[];
  readonly deadlines: readonly Deadline[];
  readonly me: Me;
  /** Whether this role holds `job:update` at organisation scope — see `AssignmentPanel`. */
  readonly mayAssign: boolean;
  /** Fetched only when `mayAssign`, so a field role never pays for a list it cannot use. */
  readonly colleagues: readonly Colleague[];
  readonly notes: readonly string[];
}

/**
 * One job: where it is, what it owes, and what can be done to it next.
 *
 * The sub-reads are settled independently rather than awaited together, because
 * a surveyor holds the job but not the organisation-wide deadline board, and a
 * role may hold `job:read` without holding `contact:read` over the customer's
 * name and address. Failing the whole screen on either 403 would make the job
 * unopenable for the role that most needs it; instead the section says why it is
 * empty, and names the grant that was withheld.
 */
export function Job({ id }: { readonly id: string }): ReactNode {
  const { client, known } = usePortal();
  const session = useSession();
  const organisationId = session?.organisationId ?? null;

  const { state, reload } = useAsync<JobDetail>(async () => {
    const job = await client.getJob(id);
    const notes: string[] = [];

    // Somebody opened this job, so the pipeline's unread mark on it is spent.
    // Deliberately not awaited and deliberately swallowed: this is bookkeeping
    // for a board the reader is not looking at, and a job must still open when
    // it fails. The worst case is a row that stays marked, which is a great
    // deal better than a job that will not load because a mark would not
    // clear.
    void client.markJobViewed(id).catch(() => {});

    const me = await client.me();
    // Asked of the same matrix the API enforces: `job:update` without an
    // `isAssigned` claim is the organisation-scope grant only — office, admin,
    // owner and technical-supervisor hold it; a surveyor or installer's "own"
    // scope never satisfies a check with no assignment asserted, which is
    // deliberate — dispatch is not something being booked onto a job grants.
    const mayAssign = can({ role: me.role, resource: "job", action: "update" });

    const [customerResult, taskResult, deadlineResult, colleagueResult] =
      await Promise.allSettled([
        client.jobCustomer(id),
        client.jobTasks(id),
        client.deadlines(),
        mayAssign ? client.colleagues() : Promise.resolve([] as Colleague[]),
      ]);

    const customer = settleOne(customerResult, notes, "the customer");
    const tasks = settle(taskResult, notes, "the task list");
    const allDeadlines = settle(deadlineResult, notes, "the deadline board");
    const colleagues = mayAssign
      ? settle(colleagueResult, notes, "the colleague list")
      : [];

    return {
      job,
      customer,
      tasks,
      deadlines: allDeadlines.filter((d) => d.job_id === id),
      me,
      mayAssign,
      colleagues,
      notes,
    };
  }, [client, id]);

  // Remembered only once the API has confirmed the job exists and is readable,
  // so a mistyped id never joins the pipeline board as a permanent 404.
  const loadedId = state.status === "ready" ? state.data.job.id : null;
  useEffect(() => {
    if (loadedId !== null) known.remember("job", organisationId, loadedId);
  }, [known, organisationId, loadedId]);

  return (
    <Async state={state} what="this job" reload={reload}>
      {(detail) => <Detail detail={detail} reload={reload} />}
    </Async>
  );
}

function settle<T>(
  result: PromiseSettledResult<T[]>,
  notes: string[],
  what: string,
): readonly T[] {
  if (result.status === "fulfilled") return result.value;
  note(result.reason, notes, what);
  return [];
}

/** The same, for a read that answers one record rather than a list. */
function settleOne<T>(
  result: PromiseSettledResult<T>,
  notes: string[],
  what: string,
): T | null {
  if (result.status === "fulfilled") return result.value;
  note(result.reason, notes, what);
  return null;
}

function note(error: unknown, notes: string[], what: string): void {
  if (error instanceof ApiError && error.status === 403) {
    const refused = refusedAction(error);
    notes.push(
      refused
        ? `${what} is not shown: your role may not ${refused.action} ${refused.resource}.`
        : `${what} is not visible to your role.`,
    );
  } else if (error instanceof ApiUnreachableError) {
    notes.push(`${what} could not be reached.`);
  } else if (error instanceof ApiError) {
    notes.push(`${what} could not be loaded: ${error.message}`);
  } else {
    notes.push(`${what} could not be loaded.`);
  }
}

function Detail({
  detail,
  reload,
}: {
  readonly detail: JobDetail;
  readonly reload: () => void;
}): ReactNode {
  const { job, customer, me, tasks, deadlines, mayAssign, colleagues, notes } =
    detail;
  const { known } = usePortal();
  const session = useSession();
  const surveys = known.listForJob("survey", session?.organisationId ?? null, job.id);

  return (
    <>
      <div className="page-header">
        <p className="small">
          <a href={href.surveys()}>Surveys</a>{" "}
          <span className="muted">·</span>{" "}
          <a href={href.jobDocuments(job.id)}>Documents</a>{" "}
          <span className="muted">·</span>{" "}
          <a href={href.jobPhotos(job.id)}>Photographic record</a>
        </p>
        <h1>{job.reference}</h1>
        <p className="lede">
          {STAGE_LABEL[job.stage]}
          {isClosed(job.stage) ? <span className="muted"> · closed</span> : null}
        </p>
      </div>

      {notes.length > 0 ? (
        <div className="notice" role="note">
          <ul className="small">
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <section>
        <h2>Record</h2>
        <dl className="fields">
          <div className="row">
            <dt>Design sign-off</dt>
            <dd>
              {job.design_signed_off_at ? (
                longDate(job.design_signed_off_at)
              ) : (
                <span className="muted">Not signed off</span>
              )}
            </dd>
          </div>
          <div className="row">
            <dt>Commissioned</dt>
            <dd>
              {job.commissioned_on ? (
                longDate(job.commissioned_on)
              ) : (
                <span className="muted">Not commissioned</span>
              )}
            </dd>
          </div>
          <div className="row">
            <dt>Assigned surveyor</dt>
            <dd>
              <Assignee id={job.assigned_surveyor_id} colleagues={colleagues} />
            </dd>
          </div>
          <div className="row">
            <dt>Assigned installer</dt>
            <dd>
              <Assignee id={job.assigned_installer_id} colleagues={colleagues} />
            </dd>
          </div>
        </dl>
      </section>

      <AssignmentPanel
        job={job}
        colleagues={colleagues}
        mayAssign={mayAssign}
        reload={reload}
      />

      <ContractPanel jobId={job.id} />

      <Customer customer={customer} me={me} jobId={job.id} reload={reload} />

      <StageActions job={job} reload={reload} />

      <section>
        <h2>
          Compliance <span className="count">{deadlines.length}</span>
        </h2>
        {deadlines.length === 0 ? (
          <p className="empty">
            No clock is running on this job. Deadlines begin at commissioning and
            at installation.
          </p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Obligation</th>
                  <th scope="col">Due</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {deadlines.map((deadline) => (
                  <tr
                    key={deadline.kind}
                    className={deadline.days_remaining < 0 ? "is-breached" : undefined}
                  >
                    <th scope="row">
                      {ruleFor(deadline.kind).label}
                      <br />
                      <span className="standard small">
                        {ruleFor(deadline.kind).authority}
                      </span>
                    </th>
                    <td className="ts">{isoDay(deadline.due_at)}</td>
                    <td>
                      <UrgencyBadge daysRemaining={deadline.days_remaining} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2>
          Tasks <span className="count">{tasks.length}</span>
        </h2>
        {tasks.length === 0 ? (
          <p className="empty">No tasks on this job.</p>
        ) : (
          <ul className="stacked">
            {tasks.map((task) => (
              <li key={task.id} className={task.completed_at ? "muted" : undefined}>
                <PriorityBadge priority={task.priority} /> {task.title}
                {task.due_at ? (
                  <span className="muted small"> · due {isoDay(task.due_at)}</span>
                ) : null}
                {task.completed_at ? (
                  <span className="muted small"> · done</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <JobThreads jobId={job.id} />

      <Surveys jobId={job.id} known={surveys} role={me.role} />
    </>
  );
}

/**
 * Every survey attached to this job.
 *
 * Reads `GET /api/jobs/:jobId/surveys` — the authoritative list — and falls
 * back to `KnownRecords` (the ids this browser has opened before) only when
 * the API refuses the read: a surveyor may hold `survey:read` at "own" scope
 * without an organisation-wide list, so a 403 there is a real state, not a
 * failure. The local list is deliberately narrower — an id remembered from a
 * previous session opened on this device — so it can never overstate what the
 * API would show.
 */
function Surveys({
  jobId,
  known,
  role,
}: {
  readonly jobId: string;
  readonly known: readonly string[];
  readonly role: Me["role"];
}): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<readonly JobSurveySummary[]>(
    () => client.jobSurveys(jobId),
    [client, jobId],
  );

  return (
    <section>
      <h2>Surveys</h2>
      <Async state={state} what="the surveys on this job" reload={reload}>
        {(surveys) => (
          <>
            <SurveysList surveys={surveys} known={known} reload={reload} />
            <CombineFloors surveys={surveys} role={role} onMerged={reload} />
            <MergeJob jobId={jobId} role={role} onMerged={reload} />
            <StartSurvey jobId={jobId} role={role} onStarted={reload} />
          </>
        )}
      </Async>
    </section>
  );
}

/**
 * MIS 3005-D Table 2, the eight locations and their two columns.
 *
 * Copied here rather than fetched because the survey does not exist yet, and
 * every route that serves this table hangs off a survey id. Column B is the
 * 99.6% figure; picking it is what waives the intermittent-heating uplift
 * (§3.4.1(a)), which is why it is the default.
 */
const TABLE_2 = [
  { location: "Belfast", A: -1.5, B: -3.2 },
  { location: "Birmingham", A: -3.2, B: -5.1 },
  { location: "Cardiff", A: -1.5, B: -3.1 },
  { location: "Edinburgh", A: -3.2, B: -5.4 },
  { location: "Glasgow", A: -3.5, B: -5.9 },
  { location: "London", A: -1.7, B: -3.0 },
  { location: "Manchester", A: -2.7, B: -4.5 },
  { location: "Plymouth", A: -0.2, B: -1.5 },
] as const;

/**
 * Starts a survey on a job that has none.
 *
 * Deliberately asks for the few things a survey cannot be created without,
 * and nothing else: the date it was surveyed, where it is (which sets the
 * outdoor design temperature from Table 2), and the annual average outdoor
 * temperature that solid ground floors are calculated against. Everything
 * else — rooms, fabric, the site survey, thermal bridging — is added on the
 * survey itself afterwards.
 */
/**
 * Folding another job into this one.
 *
 * Surveyors have been starting a new JOB per floor — "12 Somerset Way GF" and
 * "12 Somerset Way FL" are one house — so the platform holds two pipelines and
 * two half-dwelling heat losses. This puts the field work on one job; the
 * floors are then combined into storeys by the panel above.
 *
 * It refuses when the other job holds a document, a contract, a BUS voucher or
 * anything else that names it, and the API's message says which. That refusal
 * is shown verbatim rather than reworded: it names a specific record on a
 * specific job, which is more use than anything this screen could invent.
 */
function MergeJob({
  jobId,
  role,
  onMerged,
}: {
  readonly jobId: string;
  readonly role: Me["role"];
  readonly onMerged: () => void;
}): ReactNode {
  const { client } = usePortal();
  // The same organisation-scoped grant the API enforces. A surveyor is not
  // offered a control that always answers 403.
  const mayMerge = can({ role, resource: "job", action: "update" });
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /*
   * ONLY ASKED FOR WHEN IT CAN BE ANSWERED.
   *
   * `GET /api/jobs` is refused outright to a role scoped to its own work — a
   * surveyor's `job: read` is "own", and a bare listing has no single job
   * whose assignment could resolve it. Fetching it unconditionally meant
   * every surveyor opening any job fired a request that could only 403, to
   * fill a picker they are never shown. It broke two Job screen tests.
   *
   * The guard is inside the loader rather than around the hook, because a
   * hook called conditionally is a worse bug than the one being fixed.
   */
  const { state } = useAsync<JobRow[]>(
    () => (mayMerge ? client.jobs() : Promise.resolve([])),
    [client, mayMerge],
  );

  if (!mayMerge) return null;

  const others = state.status === "ready" ? state.data.filter((j) => j.id !== jobId) : [];

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!source) {
      setError("Choose the job to fold into this one.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const merged = await client.mergeJobs(jobId, source);
      setDone(
        `Merged. ${merged.surveysMoved} survey(s) moved here, and that job has ` +
          `left the pipeline. Combine the floors below.`,
      );
      setSource("");
      onMerged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="combine-floors">
      <summary>This property was surveyed as two jobs</summary>
      <p className="hint">
        Fold another job&rsquo;s surveys, photographs and tasks into this one.
        This job keeps its reference, its customer and its property; the other
        is kept and marked as merged, and leaves the pipeline.
      </p>

      <form className="stacked" onSubmit={submit}>
        <label>
          Job to fold in
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Choose a job…</option>
            {others.map((j) => (
              <option key={j.id} value={j.id}>
                {j.reference}
              </option>
            ))}
          </select>
        </label>

        {error ? <p className="error">{error}</p> : null}
        {done ? <p className="ok">{done}</p> : null}

        <p>
          <button type="submit" disabled={busy}>
            {busy ? "Merging…" : "Merge into this job"}
          </button>
        </p>
      </form>
    </details>
  );
}

/** Ground, first, second… the floors a domestic survey is cut into. */
const FLOORS: readonly { readonly level: number; readonly label: string }[] = [
  { level: -1, label: "Cellar" },
  { level: 0, label: "Ground floor" },
  { level: 1, label: "First floor" },
  { level: 2, label: "Second floor" },
  { level: 3, label: "Third floor" },
];

/**
 * Putting a property's floors into one survey.
 *
 * A survey holds one scan and one scan is one walk, so a house arrives as a
 * survey per floor. The heat loss for a dwelling is the whole dwelling, so
 * this is how the floors go back together.
 *
 * Shown only when the job has two or more surveys, because with one there is
 * nothing to combine and an explanation of combining would be clutter on every
 * other job in the system.
 */
function CombineFloors({
  surveys,
  role,
  onMerged,
}: {
  readonly surveys: readonly JobSurveySummary[];
  readonly role: Me["role"];
  readonly onMerged: () => void;
}): ReactNode {
  const { client } = usePortal();
  const mayMerge = can({ role, resource: "survey", action: "update" });
  const [target, setTarget] = useState(surveys[0]?.id ?? "");
  const [source, setSource] = useState(surveys[1]?.id ?? "");
  const [level, setLevel] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (surveys.length < 2 || !mayMerge) return null;

  const label = (s: JobSurveySummary): string => {
    const when = s.surveyedOn ?? s.createdAt.slice(0, 10);
    return s.syncedBy?.name ? `${when} — ${s.syncedBy.name}` : when;
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (target === source) {
      setError("Those are the same survey. Choose the other one to add.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const merged = await client.mergeSurveys(target, {
        sourceSurveyId: source,
        level,
      });
      setDone(
        `${merged.roomsMoved} room(s) moved onto ${merged.storeyName}. ` +
          `The combined survey's heat loss now covers that floor too.`,
      );
      onMerged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="combine-floors">
      <summary>Combine into one property survey</summary>
      <p className="hint">
        A property surveyed a floor at a time can be put back together, so the
        heat loss covers the whole dwelling. Nothing is deleted: the survey you
        add keeps its own record and is marked as merged.
      </p>

      <form className="stacked" onSubmit={submit}>
        <label>
          Keep everything on
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            {surveys.map((s) => (
              <option key={s.id} value={s.id}>
                {label(s)}
              </option>
            ))}
          </select>
        </label>

        <label>
          Add this survey
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            {surveys
              .filter((s) => s.id !== target)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {label(s)}
                </option>
              ))}
          </select>
        </label>

        <label>
          As
          <select value={level} onChange={(e) => setLevel(Number(e.target.value))}>
            {FLOORS.map((f) => (
              <option key={f.level} value={f.level}>
                {f.label}
              </option>
            ))}
          </select>
        </label>

        {/* Said before the button, not after the merge. The plan is drawn from
            one scan per survey, so the floor being added will have rooms and a
            heat loss but no drawing — and a designer finding that out by
            opening a half-drawn plan is how trust in the plan goes. */}
        <p className="hint">
          The floor you add will have its rooms and its heat loss, but no plan
          sheet: a plan is drawn from one scan, and this survey&rsquo;s is the
          floor it already had.
        </p>

        {error ? <p className="error">{error}</p> : null}
        {done ? <p className="ok">{done}</p> : null}

        <p>
          <button type="submit" disabled={busy}>
            {busy ? "Combining…" : "Combine"}
          </button>
        </p>
      </form>
    </details>
  );
}

function StartSurvey({
  jobId,
  role,
  onStarted,
}: {
  readonly jobId: string;
  readonly role: Me["role"];
  readonly onStarted: () => void;
}): ReactNode {
  const { client } = usePortal();
  // Asked of the same matrix the API enforces. Office holds survey:read only,
  // so for that role this would be a button that always answers 403 — a
  // sentence is the honest control, as with assignment above.
  const mayStart = can({ role, resource: "survey", action: "create" });
  const [open, setOpen] = useState(false);
  const [surveyedOn, setSurveyedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [location, setLocation] = useState<string>("");
  const [column, setColumn] = useState<"A" | "B">("B");
  const [annual, setAnnual] = useState("10.4");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const row = TABLE_2.find((r) => r.location === location);
  const temp = row ? row[column] : null;

  if (!mayStart) {
    return (
      <p className="muted small">
        Your role may not start a survey. This is a permission, not a fault:
        ask an administrator, or capture it on the phone.
      </p>
    );
  }

  if (!open) {
    return (
      <p>
        <button type="button" onClick={() => setOpen(true)}>
          Start a survey
        </button>
      </p>
    );
  }

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (temp === null) {
      setError("Choose where the property is, so the outdoor design temperature can be set.");
      return;
    }
    const annualValue = Number(annual);
    if (!Number.isFinite(annualValue)) {
      setError("The annual average outdoor temperature must be a number.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await client.createSurvey(jobId, {
        surveyedOn,
        externalDesignTempC: temp,
        annualAverageExternalTempC: annualValue,
        externalDesignColumn: column,
      });
      setOpen(false);
      onStarted();
      globalThis.location.hash = href.survey(created.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stacked" onSubmit={submit}>
      <label>
        Surveyed on
        <input
          type="date"
          value={surveyedOn}
          onChange={(e) => setSurveyedOn(e.target.value)}
          disabled={busy}
          required
        />
      </label>
      <label>
        Nearest Table 2 location
        <select value={location} onChange={(e) => setLocation(e.target.value)} disabled={busy} required>
          <option value="">Choose one</option>
          {TABLE_2.map((r) => (
            <option key={r.location} value={r.location}>
              {r.location}
            </option>
          ))}
        </select>
      </label>
      <label>
        Design column
        <select value={column} onChange={(e) => setColumn(e.target.value as "A" | "B")} disabled={busy}>
          <option value="B">B — 99.6% of hours</option>
          <option value="A">A — 99% of hours</option>
        </select>
      </label>
      <p className="small muted">
        {temp === null
          ? "The outdoor design temperature comes from MIS 3005-D Table 2 once a location is chosen."
          : "Outdoor design temperature " + temp.toFixed(1) + " °C, from MIS 3005-D Table 2."}
      </p>
      <label>
        Annual average outdoor temperature
        <input
          type="number"
          step="0.1"
          value={annual}
          onChange={(e) => setAnnual(e.target.value)}
          disabled={busy}
          required
        />
      </label>
      <p className="small muted">
        Used for solid ground floors (MIS 3005-D §3.4.1(c)). Change it if you
        hold the figure for this location. All of these can be changed on the
        survey afterwards.
      </p>
      {error === null ? null : (
        <p className="notice fail" role="alert">
          {error}
        </p>
      )}
      <p>
        <button type="submit" disabled={busy}>
          {busy ? "Starting…" : "Start the survey"}
        </button>{" "}
        <button type="button" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </button>
      </p>
    </form>
  );
}

function SurveysList({
  surveys,
  known,
  reload,
}: {
  readonly surveys: readonly JobSurveySummary[];
  readonly known: readonly string[];
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  if (surveys.length === 0) {
    // The local list is only worth showing when the API has none — a survey
    // opened on this device before the API grew this route, or one that a
    // race with the field app's upload has not yet landed. Anything else is
    // duplicated and misleading.
    if (known.length > 0) {
      return (
        <>
          <p className="empty">
            No surveys captured for this job yet. This browser remembers ids
            opened on this device that the API has not yet listed:
          </p>
          <ul className="stacked">
            {known.map((id) => (
              <li key={id}>
                <a href={href.survey(id)} className="mono small">
                  {id}
                </a>
              </li>
            ))}
          </ul>
        </>
      );
    }
    return <p className="empty">No surveys captured for this job yet.</p>;
  }

  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th scope="col">Survey</th>
            <th scope="col">Surveyed</th>
            <th scope="col">Synced by</th>
            <th scope="col">Open</th>
            <th scope="col">Delete</th>
          </tr>
        </thead>
        <tbody>
          {surveys.map((survey) => (
            <tr key={survey.id}>
              <th scope="row">
                <a href={href.survey(survey.id)}>
                  {survey.externalRef ?? "Survey"}
                </a>
              </th>
              <td>
                {survey.surveyedOn ? (
                  longDate(survey.surveyedOn)
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
              <td>
                {survey.syncedBy === undefined ? (
                  <span className="muted">—</span>
                ) : (
                  <>
                    {survey.syncedBy?.name ?? "A former team member"}
                    <span className="muted small"> · {longDate(survey.createdAt)}</span>
                  </>
                )}
              </td>
              <td>
                <a href={href.survey(survey.id)}>Open</a>
              </td>
              <td>
                <DeleteAction
                  what="survey"
                  warning={
                    <>
                      Everything under it goes too: rooms, surfaces,
                      photographs, scans and share links.
                    </>
                  }
                  onDelete={(reason) => client.deleteSurvey(survey.id, reason)}
                  onDone={reload}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A booked user id, read back to a name where this browser has the colleague
 * list to do it with. A role that cannot assign never fetches `colleagues` —
 * see `Job`'s data load — so this falls back to the bare id for them, exactly
 * as it always has.
 */
function Assignee({
  id,
  colleagues,
}: {
  readonly id: string | null;
  readonly colleagues: readonly Colleague[];
}): ReactNode {
  if (id === null) return <span className="muted">Nobody</span>;
  const known = colleagues.find((c) => c.userId === id);
  return known ? (
    <span>{known.fullName}</span>
  ) : (
    <span className="mono small">{id}</span>
  );
}

/**
 * Books or releases the surveyor and installer on this job.
 *
 * Gated on `mayAssign`, computed in `Job`'s data load from the same permission
 * matrix the API enforces: `job:update` asked with no `isAssigned` claim, which
 * only an organisation-scoped role (office, admin, owner, technical-supervisor)
 * ever satisfies. A surveyor or installer — including the one already booked on
 * this very job — sees a sentence instead, because being booked onto a job is
 * not the same grant as being the one who books it.
 *
 * The dropdowns are built from `colleagues` (`GET /api/chat/colleagues`), the
 * one existing route that already lists everyone in the organisation by name.
 * Filtered to the matching role, but the current holder of the slot is kept
 * even if their role does not match — a membership can change after a booking
 * without the booking itself needing to be corrected first.
 */
/**
 * The contract, on the job, where somebody looks for it.
 *
 * It was reachable before — from a link at the top of the job's Documents
 * screen, which is itself reached from a link at the top of this one. Two
 * hops through breadcrumbs that read like a trail showing where you are
 * rather than a way to go somewhere else, and the person who needed it went
 * round the houses three times before finding it. A thing people look for on
 * the job belongs on the job.
 *
 * Deliberately a signpost and not a summary, except for the one thing it
 * cannot be a signpost to: the link minted when the contract is issued, which
 * the server keeps only as a hash and so exists in readable form once.
 *
 * The rest: It could show whether terms are
 * recorded, whether a contract exists and whether it has been signed — but
 * each of those is another request on a screen that already makes several,
 * and being told "not recorded" is no more use than a door marked with where
 * it goes. The screens behind it say what is missing, in the words the
 * template refuses in.
 */
function ContractPanel({ jobId }: { readonly jobId: string }): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();

  /**
   * The terms the contract still needs, as a list rather than a sentence.
   *
   * The API has always sent these as data — `details.missing` — and the
   * generic failure panel printed the joined sentence instead, so eighteen
   * requirements arrived as one dense paragraph that nobody could work
   * through. They are the same words; the shape is the whole difference.
   */
  const missing =
    error instanceof ApiError && error.code === "incomplete_document"
      ? ((error.details?.["missing"] as string[] | undefined) ?? null)
      : null;

  /**
   * One press: produce the agreement, fix it as evidence, raise the signature
   * request and mint the customer's link.
   *
   * THE LINK IS SHOWN, NOT FOLLOWED. It used to navigate straight to the
   * agreement, for the installer standing in somebody's kitchen who turns the
   * device round for the customer to sign on the spot. That case is real and
   * is still one press away below - but it was the ONLY case the screen
   * allowed, and it threw the link away in the act of using it. Most contracts
   * are not signed in the kitchen: they are sent, and the person sending one
   * had no way to get hold of the address to send.
   *
   * The server keeps only the token's hash, so this is the one moment the link
   * exists in readable form. It is held in state rather than navigated to,
   * because leaving the page is what destroyed it.
   */
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null);

  /**
   * TWO DOCUMENTS, ONE PANEL. The contract of sale and the company's own
   * simple agreement are issued identically and produce the same thing -- a
   * link for one customer, shown once -- so they share the panel below rather
   * than growing a second copy of it that could drift.
   */
  const issue = async (kind: "contract" | "agreement"): Promise<void> => {
    await submit(async () => {
      const issued =
        kind === "contract"
          ? await client.issueContract(jobId)
          : await client.issueCustomerAgreement(jobId);
      setLink({ url: issued.url, expiresAt: issued.expiresAt });
    });
  };

  return (
    <section>
      <h2>Contract</h2>
      {missing !== null ? (
        <StillNeeded items={missing} />
      ) : error !== null ? (
        <Failure error={error} what="issuing that document" />
      ) : null}
      {link === null ? (
        <>
          <p className="small muted">
            Produces the agreement and the customer&rsquo;s own link to sign it.
            Send them the link, or open it here and turn the screen round. A
            signed PDF is kept with the job either way.
          </p>
          <p className="row">
            <button type="button" disabled={busy} onClick={() => void issue("contract")}>
              {busy ? "Preparing…" : "Issue contract"}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => void issue("agreement")}
            >
              {busy ? "Preparing…" : "Issue your own agreement"}
            </button>
          </p>
          <p className="hint small">
            The agreement is your own shorter document: it needs only the
            customer&rsquo;s name, email and address, and the wording you set
            once under Company. The contract of sale carries the price, the
            timetable and the statutory cancellation terms, and asks for them.
          </p>
        </>
      ) : (
        <IssuedContractLink link={link} onIssueAnother={() => setLink(null)} />
      )}
    </section>
  );
}

/**
 * What the contract is still waiting on, and where to go and answer it.
 *
 * Not a wall of prose. Each item is a thing somebody has to go and decide,
 * so each gets its own line and the way to the form sits at the bottom
 * rather than being hunted for.
 */
function StillNeeded({ items }: { readonly items: readonly string[] }): ReactNode {
  return (
    <div className="notice warn" role="alert">
      <p>
        <strong>
          {items.length === 1
            ? "One thing is still needed before this contract can be produced."
            : `${items.length} things are still needed before this contract can be produced.`}
        </strong>{" "}
        Each one goes on the agreement, so none of them can be guessed.
      </p>
      <ul>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <p>
        <a className="cta" href={href.company()}>
          Set these once on Company
        </a>
      </p>
    </div>
  );
}

function AssignmentPanel({
  job,
  colleagues,
  mayAssign,
  reload,
}: {
  readonly job: JobRow;
  readonly colleagues: readonly Colleague[];
  readonly mayAssign: boolean;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const [surveyorId, setSurveyorId] = useState(job.assigned_surveyor_id ?? "");
  const [installerId, setInstallerId] = useState(job.assigned_installer_id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Kept in step with the record, not just the mount: a reload after a save,
  // or after any other change to the job, must not leave the form showing what
  // it last submitted rather than what the API now holds.
  useEffect(() => {
    setSurveyorId(job.assigned_surveyor_id ?? "");
    setInstallerId(job.assigned_installer_id ?? "");
  }, [job.assigned_surveyor_id, job.assigned_installer_id]);

  if (!mayAssign) {
    return (
      <section>
        <h2>Assignment</h2>
        <p className="muted small">
          Your role may not book a surveyor or an installer onto a job. This is
          a permission, not a fault: ask somebody in the office.
        </p>
      </section>
    );
  }

  const surveyors = colleagues.filter(
    (c) => c.role === "surveyor" || c.userId === job.assigned_surveyor_id,
  );
  const installers = colleagues.filter(
    (c) => c.role === "installer" || c.userId === job.assigned_installer_id,
  );

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await client.assignJob(job.id, {
        assignedSurveyorId: surveyorId === "" ? null : surveyorId,
        assignedInstallerId: installerId === "" ? null : installerId,
      });
      reload();
    } catch (cause: unknown) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h2>Assignment</h2>
      <form onSubmit={(e) => void onSubmit(e)}>
        <div className="field">
          <label htmlFor="assign-surveyor">Surveyor</label>
          <select
            id="assign-surveyor"
            value={surveyorId}
            onChange={(e) => setSurveyorId(e.target.value)}
          >
            <option value="">Nobody</option>
            {surveyors.map((c) => (
              <option key={c.userId} value={c.userId}>
                {c.fullName}
                {c.active ? "" : " (no longer active)"}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="assign-installer">Installer</label>
          <select
            id="assign-installer"
            value={installerId}
            onChange={(e) => setInstallerId(e.target.value)}
          >
            <option value="">Nobody</option>
            {installers.map((c) => (
              <option key={c.userId} value={c.userId}>
                {c.fullName}
                {c.active ? "" : " (no longer active)"}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save assignment"}
        </button>
      </form>
      {error !== null ? <Failure error={error} what="that assignment" /> : null}
    </section>
  );
}

/**
 * Who the job is for, and where.
 *
 * `GET /api/jobs/:id/customer` answers this in three states rather than two, and
 * all three are rendered differently on purpose. A recorded customer is shown. A
 * job with none says so. A job whose customer this organisation may not read —
 * which is what a subcontractor working an umbrella’s shared job sees, because
 * `contacts_isolation` admits only the owning organisation — says *that*, because
 * showing it as "none recorded" is how the same customer gets entered twice.
 *
 * A null `customer` is different again: the read itself was refused, and the
 * note at the top of the screen already names the grant.
 */
function Customer({
  customer,
  me,
  jobId,
  reload,
}: {
  readonly customer: JobCustomer | null;
  readonly me: Me;
  readonly jobId: string;
  readonly reload: () => void;
}): ReactNode {
  const [editing, setEditing] = useState(false);
  // The same matrix the API enforces: the customer record is the office's to
  // own, so a surveyor reads the address they must drive to and changes none of
  // it. Offering a form the API would refuse is how a button teaches distrust.
  const mayEdit =
    can({ role: me.role, resource: "job", action: "update" }) &&
    can({ role: me.role, resource: "contact", action: "create" });

  if (customer === null) {
    return (
      <section>
        <h2>Customer</h2>
        <p className="empty">
          Not shown. The reason is listed at the top of this screen.
        </p>
      </section>
    );
  }

  const { contact, property } = customer;

  if (editing) {
    return (
      <CustomerForm
        jobId={jobId}
        customer={customer}
        onDone={() => {
          setEditing(false);
          reload();
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <section>
      <div className="survey-header-row">
        <h2>Customer</h2>
        {mayEdit ? (
          <button type="button" className="secondary" onClick={() => setEditing(true)}>
            {contact === null && property === null ? "Record customer & address" : "Edit"}
          </button>
        ) : null}
      </div>
      {property === null && !customer.propertyWithheld ? (
        <p className="notice pending">
          <strong>No address yet.</strong> A survey captured on the phone names
          itself with whatever was typed on site, and creates no address. The
          design, a BUS application and handing this survey to another portal all
          need one.
        </p>
      ) : null}
      <dl className="fields">
        <div className="row">
          <dt>Name</dt>
          <dd>
            {contact ? (
              contact.fullName
            ) : customer.contactWithheld ? (
              <Withheld what="contact" />
            ) : (
              <span className="muted">No customer recorded</span>
            )}
          </dd>
        </div>
        <div className="row">
          <dt>Email</dt>
          <dd>{contact?.email ?? <span className="muted">Not recorded</span>}</dd>
        </div>
        <div className="row">
          <dt>Telephone</dt>
          <dd>{contact?.phone ?? <span className="muted">Not recorded</span>}</dd>
        </div>
        <div className="row">
          <dt>Address</dt>
          <dd>
            {property ? (
              <>
                {[property.addressLine1, property.addressLine2, property.town]
                  .filter((part): part is string => part !== null && part !== "")
                  .join(", ")}
                <br />
                <span className="mono">{property.postcode}</span>
              </>
            ) : customer.propertyWithheld ? (
              <Withheld what="property" />
            ) : (
              <span className="muted">No property recorded</span>
            )}
          </dd>
        </div>
        <div className="row">
          <dt>Age band</dt>
          <dd>
            {property?.ageBand ?? (
              <span className="muted">Not recorded</span>
            )}
          </dd>
        </div>
        <div className="row">
          <dt>UPRN</dt>
          <dd>
            {property?.uprn ? (
              <span className="mono">{property.uprn}</span>
            ) : (
              <span className="muted">Not recorded</span>
            )}
          </dd>
        </div>
        <div className="row">
          <dt>EPC</dt>
          <dd>
            {property?.epcReference ?? (
              <span className="muted">None on file</span>
            )}
          </dd>
        </div>
      </dl>
    </section>
  );
}

/**
 * The customer and the address, as a form.
 *
 * ONE SUBMIT FOR BOTH HALVES, and each half sent only if it has been filled in:
 * `PATCH /api/jobs/:id/customer` leaves out what it is not given, so a job that
 * has a customer but no address can be given one without the form having to
 * re-assert the name — and a half nobody typed into is never sent as blanks.
 *
 * Pre-filled from what is already recorded, so this is the edit screen as well
 * as the first-entry screen. A withheld half (an umbrella's shared job) is not
 * offered for editing at all: it is not this organisation's record to rewrite,
 * and the API would refuse it.
 */
function CustomerForm({
  jobId,
  customer,
  onDone,
  onCancel,
}: {
  readonly jobId: string;
  readonly customer: JobCustomer;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { contact, property } = customer;

  const [fullName, setFullName] = useState(contact?.fullName ?? "");
  const [email, setEmail] = useState(contact?.email ?? "");
  const [phone, setPhone] = useState(contact?.phone ?? "");
  const [addressLine1, setAddressLine1] = useState(property?.addressLine1 ?? "");
  const [addressLine2, setAddressLine2] = useState(property?.addressLine2 ?? "");
  const [town, setTown] = useState(property?.town ?? "");
  const [postcode, setPostcode] = useState(property?.postcode ?? "");
  const [uprn, setUprn] = useState(property?.uprn ?? "");
  const [epcReference, setEpcReference] = useState(property?.epcReference ?? "");
  const [fuel, setFuel] = useState<string>(property?.previousHeatingFuel ?? "");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const patch: JobCustomerPatch = {
        ...(fullName.trim() !== "" && !customer.contactWithheld
          ? { customer: { fullName, email: email || null, phone: phone || null } }
          : {}),
        ...(addressLine1.trim() !== "" && postcode.trim() !== "" && !customer.propertyWithheld
          ? {
              property: {
                addressLine1,
                addressLine2: addressLine2 || null,
                town: town || null,
                postcode,
                uprn: uprn || null,
                epcReference: epcReference || null,
                previousHeatingFuel: (fuel || null) as PreviousHeatingFuel | null,
              },
            }
          : {}),
      };
      if (patch.customer === undefined && patch.property === undefined) {
        // Said here rather than as a 400, because the form knows exactly which
        // two fields an address needs and the API's message cannot.
        throw new Error(
          "Enter a customer name, or a first line of address and a postcode — or both.",
        );
      }
      await client.setJobCustomer(jobId, patch);
      onDone();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h2>Customer &amp; address</h2>
      <form onSubmit={(e) => void submit(e)}>
        <div className="field">
          <label htmlFor="cust-name">Customer name</label>
          <input
            id="cust-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            disabled={customer.contactWithheld}
          />
        </div>
        <div className="field">
          <label htmlFor="cust-email">Email</label>
          <input id="cust-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cust-phone">Telephone</label>
          <input id="cust-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>

        <div className="field">
          <label htmlFor="prop-line1">Address</label>
          <input
            id="prop-line1"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            placeholder="14 Rowan Close"
            disabled={customer.propertyWithheld}
          />
        </div>
        <div className="field">
          <label htmlFor="prop-line2">Address line 2</label>
          <input id="prop-line2" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="prop-town">Town</label>
          <input id="prop-town" value={town} onChange={(e) => setTown(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="prop-postcode">Postcode</label>
          <input
            id="prop-postcode"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="LS6 2AB"
          />
        </div>
        <div className="field">
          <label htmlFor="prop-uprn">UPRN</label>
          <input
            id="prop-uprn"
            value={uprn}
            onChange={(e) => setUprn(e.target.value)}
            inputMode="numeric"
            placeholder="100051234567"
          />
          <p className="small muted">
            The property&rsquo;s unique reference number, if you have it. What another
            system matches this house on when the address is spelled differently.
          </p>
        </div>
        <div className="field">
          <label htmlFor="prop-epc">EPC reference</label>
          <input id="prop-epc" value={epcReference} onChange={(e) => setEpcReference(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="prop-fuel">Heating being replaced</label>
          <select id="prop-fuel" value={fuel} onChange={(e) => setFuel(e.target.value)}>
            <option value="">Not recorded</option>
            {PREVIOUS_HEATING_FUELS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <div className="actions">
          <button type="submit" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
          <button type="button" className="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
        {error !== null ? <Failure error={error} what="saving the customer and address" /> : null}
      </form>
    </section>
  );
}

function Withheld({ what }: { readonly what: string }): ReactNode {
  return (
    <span className="pending">
      Recorded, but held by the organisation that owns this job. A share carries
      the job, not the {what}.
    </span>
  );
}

/**
 * The moves this job can make, taken from the API's own transition table.
 *
 * Where a move starts a compliance clock the API demands the date, so the form
 * asks for it. Finding that out through a 400 after pressing the button is a
 * worse way to learn it.
 */
function StageActions({
  job,
  reload,
}: {
  readonly job: JobRow;
  readonly reload: () => void;
}): ReactNode {
  const { client } = usePortal();
  const moves = nextStages(job.stage);
  const [to, setTo] = useState<JobStage | "">("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const required = to === "" ? null : requiredDateField(to);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (to === "") return;
    setBusy(true);
    setError(null);
    try {
      await client.transitionJob(job.id, {
        stage: to,
        ...(required ? { [required.field]: date } : {}),
      });
      setTo("");
      setDate("");
      reload();
    } catch (cause: unknown) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function signOff(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await client.signOffDesign(job.id);
      reload();
    } catch (cause: unknown) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  const canSignOff = job.design_signed_off_at === null;

  return (
    <section>
      <h2>Move this job on</h2>

      {moves.length === 0 ? (
        <p className="empty">
          {STAGE_LABEL[job.stage]} is a final stage. There is nowhere for this
          job to go.
        </p>
      ) : (
        <form onSubmit={(e) => void submit(e)}>
          <div className="field">
            <label htmlFor="stage">Next stage</label>
            <select
              id="stage"
              value={to}
              onChange={(e) => setTo(e.target.value as JobStage | "")}
            >
              <option value="">Choose…</option>
              {moves.map((stage) => (
                <option key={stage} value={stage}>
                  {STAGE_LABEL[stage]}
                </option>
              ))}
            </select>
          </div>

          {required ? (
            <div className="field">
              <label htmlFor="date">{required.label}</label>
              <input
                id="date"
                type="date"
                required
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              <p className="hint small">
                This starts a compliance clock, so the API will not accept the
                move without it.
              </p>
            </div>
          ) : null}

          <button type="submit" disabled={busy || to === ""}>
            {busy ? "Working…" : "Move"}
          </button>
        </form>
      )}

      {canSignOff ? (
        <p className="pending">
          <button className="secondary" disabled={busy} onClick={() => void signOff()}>
            Sign off the design
          </button>
        </p>
      ) : null}

      {error !== null ? (
        <ErrorNotice error={error} />
      ) : null}
    </section>
  );
}

/**
 * A refused write, explained.
 *
 * A compliance refusal is the interesting case and it gets its own shape: the
 * rules engine names the clauses it enforced, and reprinting those is the whole
 * point of the engine. Collapsing it to "400 Bad Request" would throw away the
 * only part the installer can act on.
 */
function ErrorNotice({ error }: { readonly error: unknown }): ReactNode {
  if (error instanceof ApiError) {
    const failures = complianceFailures(error);
    if (failures.length > 0) {
      return (
        <div className="notice fail" role="alert">
          <p>The rules engine refused this move:</p>
          <ul>
            {failures.map((failure) => (
              <li key={failure.code}>
                {failure.message}{" "}
                <span className="mono small">{failure.code}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    }
  }
  return <Failure error={error} what="that change" />;
}
